import { get } from "svelte/store";
import { currentRawFirehose, currentDerivedWorkout, currentLabels, saveStatus, labelsLoadFailed, journalPendingCount, journalReplaying } from "./state";
import { deriveWorkout } from "./lapAnalysis";
import { bridge, warnToNative } from "../bridge/client";
import { appendJournal, removeJournal, readJournal, journalCount } from "./labelJournal";
import { fetchLabelsWithRetry, type LabelLoadRetryOptions } from "./labelLoadRetry";
import { showcase } from "./nav";
import type { LapLabel } from "@contract/types";

/**
 * The single live-app entry point for any lap-label change (stroke corrections
 * and exclusions alike). Optimistic: update the labels store and re-derive the
 * current workout immediately so the ribbon AND the open overlay (both read
 * currentDerivedWorkout) reflect the fix, then persist with status tracking.
 * The in-memory state holds if the bridge is unavailable (e.g. browser preview).
 *
 * Persistence is fire-and-forget from the caller's perspective (void return),
 * but internally drives saveStatus through idle → saving → saved | failed with
 * one auto-retry. A monotonic commitId ensures only the latest write updates
 * saveStatus — stale in-flight calls are silently discarded. Writes are
 * serialized through a single-flight latest-wins queue (see below) so a
 * retrying older write can never land on disk after a newer one.
 */

// Monotonic counter: each commitLabelChange call claims the next id. Only the
// call whose id matches latestCommitId at resolution time may update saveStatus.
let commitSeq = 0;
let latestCommitId = 0;

// Which swim resetSaveStatusForSwim last reset FOR. Lets a same-swim remount
// (Sets->Swim tab switch re-running Prototypes' firehose effect) recognize
// itself as a no-op rather than another swim-switch — see resetSaveStatusForSwim.
let lastResetSwimUuid: string | undefined;

type PendingWrite = { labels: LapLabel[]; uuid: string | undefined; id: number };

// Single-flight latest-wins queue (2026-07-05 endurance hardening): at most one
// bridge write is in flight; a commit that arrives meanwhile parks in the ONE
// pending slot, replacing anything already parked (the superseded snapshot is
// obsolete — the parked one contains the user's later state). A failed attempt
// is retried once, UNLESS a newer write superseded it while failing. Guarantees
// the last user action always lands on disk last — the property offline/flaky
// connectivity (locker room) breaks under fire-and-forget writes.
// Disk ordering additionally relies on the native ORDERING CONTRACT in
// BridgeRouter.swift (write before first suspension): posts in order → disk in order.
let inFlight = false;
let pending: PendingWrite | null = null;

// Failure-reason ring buffer (durable-corrections Task 6). Records the last N save
// outcomes so DebugView / a dev seam can answer "why did that correction fail?"
// after the fact. Web-side only; module-local; exposed via _saveLog().
export interface SaveOutcome {
  commitId: number;
  ok: boolean;
  // "ok" | "set-rejected" | "bridge-timeout" | "bridge-no-handler" | "bridge-rejected"
  // | "timeout-recovered" | "superseded-by-newer" | "rescue-readback-timeout"
  // | "rescue-readback-no-handler" | "rescue-readback-rejected" | "rescue-readback-mismatch"
  // | "readback-timeout" | "readback-no-handler" | "readback-rejected"
  // | "readback-mismatch" | "C2a-block" | "replay-ok" | "replay-subsumed"
  // | "replay-preread-timeout" | "replay-preread-no-handler" | "replay-preread-rejected"
  // | "replay-timeout" | "replay-no-handler"
  // | "replay-rejected" | "replay-timeout-recovered" | "replay-readback-timeout"
  // | "replay-readback-no-handler" | "replay-readback-rejected"
  // | "replay-readback-mismatch" | "replay-superseded"
  reason: string;
  ts: number;
  // Set only on replay-path entries (see doReplayJournal) — the commit path's
  // outcome is already scoped to the currently-open swim, so it's omitted there.
  workoutUUID?: string;
  // The bridge's own words for a failure (issue #10, 2026-09-14): the error
  // code + message native replied with, or the timeout text. The reason tag
  // alone ("bridge-rejected") cannot say WHICH refusal it was.
  detail?: string;
}
const SAVE_LOG_CAP = 10;
const saveLog: SaveOutcome[] = [];

function recordOutcome(commitId: number, ok: boolean, reason: string, workoutUUID?: string, detail?: string): void {
  saveLog.push({ commitId, ok, reason, ts: Date.now(), workoutUUID, detail });
  if (saveLog.length > SAVE_LOG_CAP) saveLog.shift();
  // Every failure reaches the phone's log (issue #10). The tester's TestFlight
  // report showed the pill and nothing else: this ring buffer only exists in
  // the web heap, and the DEBUG Firehose view that reads it is not on a
  // TestFlight build. The `log` bridge method (B-017) mirrors the line to
  // ClubLog, so `idevicesyslog | grep "web\["` answers "why did it fail".
  if (!ok) {
    const swim = workoutUUID ? ` swim=${workoutUUID.slice(0, 8)}` : "";
    warnToNative(`[save] commit=${commitId} ${reason}${swim}${detail ? ` — ${detail}` : ""}`);
  }
}

/** The error's own message, for the save log's `detail`. */
function errorDetail(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Dev seam: the recent save-outcome log (oldest first), for DebugView + tests. */
export function _saveLog(): SaveOutcome[] {
  return saveLog.slice();
}

declare global {
  interface Window {
    /** Dev/debug seam (durable-corrections Task 6 fix round): exposes the
     *  save-outcome ring buffer so on-device diagnosis is one console call in
     *  Safari Web Inspector (`window.__SWIMSET_SAVELOG__()`). Attached/detached
     *  as the `showcase` store flips (below) — same spirit as bridge/client.ts's
     *  dev-only window seams, withheld from the public showcase page. */
    __SWIMSET_SAVELOG__?: () => SaveOutcome[];
  }
}

// Module-eval runs before main.ts's async showcase boot path sets the
// `showcase` store, so a one-time check here would miss a showcase load
// entirely — subscribing keeps the seam in sync as the store flips.
if (typeof window !== "undefined") {
  showcase.subscribe((isShowcase) => {
    if (isShowcase) delete window.__SWIMSET_SAVELOG__;
    else window.__SWIMSET_SAVELOG__ = _saveLog;
  });
}

/** Bare discriminant off BridgeUnavailableError's `.reason` (duck-typed), or
 *  `null` for a generic thrown error that doesn't carry one. Duck-typing (not
 *  `instanceof`) because this module's `bridge` import is swapped for a
 *  `vi.mock` factory in tests that doesn't export the class, so an
 *  `instanceof` check would see `undefined` and throw. */
function bridgeReasonTag(err: unknown): "timeout" | "no-handler" | "rejected" | null {
  if (err && typeof err === "object" && "reason" in err) {
    const r = (err as { reason?: unknown }).reason;
    if (r === "timeout" || r === "no-handler" || r === "rejected") return r;
  }
  return null;
}

/** Write-path (commitLabelChange) failure reason: "bridge-<tag>" for a
 *  BridgeUnavailableError, else the generic "set-rejected". */
function bridgeFailureReason(err: unknown): string {
  const tag = bridgeReasonTag(err);
  return tag ? `bridge-${tag}` : "set-rejected";
}

/** Read-back failure reason: "readback-<tag>" for a BridgeUnavailableError
 *  surfaced by getLapLabels, else the generic "readback-rejected". Lets the
 *  save-outcome ring buffer tell a read-side bridge timeout apart from an
 *  actual data mismatch (Task 6 fix round) instead of collapsing both into
 *  "readback-mismatch". */
function readbackFailureReason(err: unknown): string {
  const tag = bridgeReasonTag(err);
  return tag ? `readback-${tag}` : "readback-rejected";
}

/** Order-independent identity of a label's user-meaningful content. Used only to
 *  compare a committed set against what the store read back. INCLUDES the
 *  composite length key (offsetSeconds/durationSeconds/duplicateOrdinal) when
 *  present, mirroring correctionKey.ts's keyEquals — lapIndex alone can
 *  collide across a re-fetch (see commit 4c55068), so two DIFFERENT physical
 *  lengths that happen to share lapIndex + every other field must still
 *  compare unequal here, or a native-side composite-key corruption could
 *  read back as verified. */
function labelIdentity(l: LapLabel): string {
  return JSON.stringify([
    l.lapIndex,
    l.kind,
    l.strokeOverride ?? null,
    l.customName ?? null,
    l.excludedFromAggregates,
    l.offsetSeconds ?? null,
    l.durationSeconds ?? null,
    l.duplicateOrdinal ?? null,
  ]);
}

/** True when `stored` represents exactly the same set of corrections as `committed`. */
function sameLabelSet(committed: LapLabel[], stored: LapLabel[]): boolean {
  if (!Array.isArray(stored) || committed.length !== stored.length) return false;
  const storedIds = new Set(stored.map(labelIdentity));
  return committed.every((l) => storedIds.has(labelIdentity(l)));
}

/** Per-lap merge key for replay reconciliation: the composite length key when
 *  present (the authoritative identity — see correctionKey.ts), else lapIndex.
 *  A same-lap pair where only one side carries the composite key merges as two
 *  labels — acceptable: that mixed state predates this code and the display
 *  layer already resolves it by key-with-fallback. */
function replayMergeKey(l: LapLabel): string {
  return l.offsetSeconds != null && l.durationSeconds != null
    ? `k:${l.offsetSeconds}:${l.durationSeconds}:${l.duplicateOrdinal ?? 0}`
    : `lap:${l.lapIndex}`;
}

/** Reconcile a journaled snapshot with native's CURRENT list (2026-07-09
 *  escalation — the 11→7 label regression). A journal entry is a full-
 *  replacement snapshot frozen at commit time; replayed verbatim through
 *  last-write-wins setLapLabels it destroys every label native gained since
 *  (the boot-replay clobber that erased laps 43/65/68/74 on the owner's
 *  device). Union by per-lap key; a same-key conflict keeps native's copy only
 *  when it is STRICTLY newer by createdAtMs — the journaled intent wins ties
 *  and the no-timestamp legacy case, preserving plain-strand replay behavior.
 *  Merge can only add or update, never delete: a stale snapshot that encoded a
 *  DELETION no longer propagates it on replay — we cannot tell "user removed
 *  X" apart from "snapshot predates X", and resurrecting a correction is
 *  recoverable while destroying one is not. Live deletes (full replacement
 *  through the commit queue) are unaffected. */
function mergeLabelsForReplay(stored: LapLabel[], entryLabels: LapLabel[]): LapLabel[] {
  const merged = new Map<string, LapLabel>();
  for (const l of stored) merged.set(replayMergeKey(l), l);
  for (const l of entryLabels) {
    const key = replayMergeKey(l);
    const existing = merged.get(key);
    if (!existing || (existing.createdAtMs ?? 0) <= (l.createdAtMs ?? 0)) merged.set(key, l);
  }
  return [...merged.values()];
}

/** Discriminated read-back outcome. Three cases, because they mean different
 *  things for the ALREADY-SUCCEEDED write that precedes the read-back:
 *   - ok           — read back matched: the write is confirmed.
 *   - "unreadable" — getLapLabels THREW (bridge timeout / no-handler / rejected).
 *     The read could not be performed. This says NOTHING about the write, which
 *     already resolved — and a resolved setLapLabels means native persisted the
 *     label synchronously and force-synchronized it BEFORE replying, so it is
 *     durably on disk. Treated as SAVED (2026-07-08 audit): collapsing this into
 *     a failure stranded the journal and raised a recurring false "didn't save"
 *     pill under WKWebView render contention, while the fix was on disk the
 *     whole time.
 *   - "mismatch"   — getLapLabels RESOLVED but returned DIFFERENT content: a
 *     real native integrity signal (e.g. composite-key corruption). Stays a
 *     failure — journal retained, pill shown. */
type ReadBackResult =
  | { ok: true }
  | { ok: false; kind: "unreadable" | "mismatch"; reason: string; detail?: string };

/** Read the labels back from native and confirm the committed set landed on disk. */
async function verifyReadBack(committed: LapLabel[], uuid: string | undefined): Promise<ReadBackResult> {
  let stored: LapLabel[];
  try {
    stored = await bridge.getLapLabels(uuid);
  } catch (err) {
    return { ok: false, kind: "unreadable", reason: readbackFailureReason(err), detail: errorDetail(err) };
  }
  return sameLabelSet(committed, stored)
    ? { ok: true }
    : {
        ok: false,
        kind: "mismatch",
        reason: "readback-mismatch",
        detail: `committed ${committed.length} label(s), read back ${Array.isArray(stored) ? stored.length : typeof stored}`,
      };
}

/** One bridge attempt: write, then read back to confirm it landed. Only a
 *  verified write drops the journal entry and marks saved. Returns false on
 *  any write failure OR an unverified read-back. Every outcome is logged to
 *  the save-outcome ring buffer with a reason that names what happened. */
async function attempt(w: PendingWrite): Promise<boolean> {
  try {
    await bridge.setLapLabels(w.labels, w.uuid);
  } catch (err) {
    recordOutcome(w.id, false, bridgeFailureReason(err), undefined, errorDetail(err));
    // Reply-lost rescue (2026-07-09, owner's deterministic false pill): a
    // TIMED-OUT write may have LANDED — native persists synchronously and
    // force-synchronizes BEFORE replying (ORDERING CONTRACT, BridgeRouter
    // .swift), so under WKWebView contention only the reply misses call()'s
    // 5s race. Verify by read-back: a read that RESOLVES and MATCHES the
    // committed set proves the write is on disk → saved, journal dropped, no
    // pill. Timeout ONLY — "rejected"/"no-handler" is a definitive refusal
    // (native replied with an error / never registered), and an unreadable or
    // MISMATCHED read-back here keeps the failure (opposite polarity to
    // unreadable-after-RESOLVED-write above: after a FAILED write, no
    // verification means nothing is proven, so the entry must survive).
    if (bridgeReasonTag(err) === "timeout") {
      const verified = await verifyReadBack(w.labels, w.uuid);
      if (verified.ok) {
        recordOutcome(w.id, true, "timeout-recovered");
        removeJournal(w.uuid, w.id);
        syncJournalCount();
        if (w.id === latestCommitId) saveStatus.set("saved");
        return true;
      }
      // Superseded while unconfirmable (2026-07-09 escalation, B-answer): a
      // newer commit for the SAME swim is already parked — its snapshot was
      // built on currentLabels and carries this write's intent forward, so
      // this commit's outcome is moot. Success-for-drain, not a failure.
      if (pending && pending.uuid === w.uuid) {
        recordOutcome(w.id, true, "superseded-by-newer");
        return true;
      }
      // Not superseded and not rescued: name the rescue's failure. Tonight's
      // field export showed two commits with "attempt+retry, NO rescue" and
      // ZERO trace of WHY each rescue read refused — this closes that hole.
      recordOutcome(w.id, false, `rescue-${verified.reason}`, undefined, verified.detail);
    }
    return false;
  }
  // The write RESOLVED above → native persisted it before replying. The
  // read-back is a content-integrity check, not a durability check. With a
  // full-list last-write-wins store, "did MY set land?" is proven by exact set
  // equality — but stored content matching a NEWER pending commit for this
  // swim is supersession (that commit now owns the swim's state), not
  // corruption. Only a mismatch with nothing newer in flight stays a failure.
  const verified = await verifyReadBack(w.labels, w.uuid);
  if (!verified.ok && verified.kind === "mismatch") {
    if (pending && pending.uuid === w.uuid) {
      recordOutcome(w.id, true, "superseded-by-newer");
      return true; // drain proceeds straight to the newer commit
    }
    recordOutcome(w.id, false, verified.reason, undefined, verified.detail);
    return false;
  }
  // ok, or "unreadable" (couldn't re-read a write that already landed): saved.
  recordOutcome(w.id, verified.ok, verified.ok ? "ok" : verified.reason);
  removeJournal(w.uuid, w.id);
  syncJournalCount();
  if (w.id === latestCommitId) saveStatus.set("saved");
  autoRetryStep = 0; // a confirmed save ends any ladder in progress
  return true;
}

async function drain(): Promise<void> {
  inFlight = true;
  while (pending) {
    const w = pending;
    pending = null;
    if (await attempt(w)) continue;
    // First attempt failed. Superseded meanwhile? Skip the retry — the newer
    // snapshot makes this one obsolete.
    if (pending) continue;
    const retried = await attempt(w);
    if (!retried && !pending && w.id === latestCommitId) saveStatus.set("failed");
  }
  inFlight = false;
  if (journalCount() > 0) armAutoRetry();
}

/**
 * "Keeping it and retrying" has to be true without a tap (issue #10, build 12,
 * 2026-09-14). Before this, the two in-line attempts above were the ONLY
 * retries a live correction got; after them the entry sat in the journal
 * until the next launch, the auth nudge, or the pill's Try now. The tester's
 * failure came 98 s into the launch window, where the 2026-07-09 audit found
 * the bridge can be deaf for a while (native boot work, four web views) and
 * then fine — the shape a delayed replay heals on its own.
 *
 * Bounded ladder, journal-driven: each step runs one replayJournal pass and
 * the next step is armed only if the journal is still non-empty afterwards.
 * A confirmed save or a new commit resets the ladder; once exhausted it stays
 * quiet until then, so a genuinely broken bridge is not hammered forever.
 */
const AUTO_RETRY_LADDER_MS = [15_000, 45_000];
let autoRetryStep = 0;
let autoRetryTimer: ReturnType<typeof setTimeout> | undefined;

function armAutoRetry(): void {
  if (autoRetryTimer !== undefined || autoRetryStep >= AUTO_RETRY_LADDER_MS.length) return;
  const delay = AUTO_RETRY_LADDER_MS[autoRetryStep++];
  autoRetryTimer = setTimeout(() => {
    autoRetryTimer = undefined;
    void replayJournal().then(() => {
      if (journalCount() > 0) armAutoRetry();
    });
  }, delay);
}

function disarmAutoRetry(): void {
  if (autoRetryTimer !== undefined) clearTimeout(autoRetryTimer);
  autoRetryTimer = undefined;
  autoRetryStep = 0;
}

/** refreshLabelsFromNative's read-back ladder: 4 reads over ~2.8 s. */
const REFRESH_BACKOFFS_MS = [400, 800, 1600];

export function commitLabelChange(next: LapLabel[]): void {
  // C2a guard: refuse to write over labels we could not read.
  if (get(labelsLoadFailed)) {
    recordOutcome(++commitSeq, false, "C2a-block");
    saveStatus.set("failed");
    // Recover instead of staying stuck: re-read native for the swim on screen.
    // A good read clears the block (and shows what disk holds) so the next tap saves.
    void refreshLabelsFromNative();
    return;
  }

  currentLabels.set(next);
  const raw = get(currentRawFirehose);
  if (raw) currentDerivedWorkout.set(deriveWorkout(raw, next));

  const id = ++commitSeq;
  latestCommitId = id;
  saveStatus.set("saving");
  // Fresh user intent: this commit gets its own two attempts and a fresh ladder.
  disarmAutoRetry();

  // Durability: journal the intent BEFORE it touches the bridge. If the process
  // dies between here and a confirmed write, replayJournal re-sends it on boot.
  appendJournal({ commitId: id, workoutUUID: raw?.workout.uuid, labels: next, ts: Date.now() });
  syncJournalCount();

  pending = { labels: next, uuid: raw?.workout.uuid, id };
  if (!inFlight) void drain();
}

/**
 * Reset save status to idle and invalidate any in-flight commit. Call this
 * when loading a new swim so a stale pending write cannot flip status after
 * the new swim is shown. (T5b wires this on swim-switch.)
 *
 * Swim-UNAWARE primitive: every call disarms in-flight suppression, even for
 * the swim already in memory. Production code should call
 * resetSaveStatusForSwim instead — this is kept exported for tests (and as
 * the building block that one uses) that want the unconditional reset.
 */
export function resetSaveStatus(): void {
  // Advance both counters past any in-flight id. Any pending persistWithRetry
  // has an id <= the old commitSeq, so after the bump id !== latestCommitId
  // and its resolution branch is a no-op.
  commitSeq += 1;
  latestCommitId = commitSeq;
  saveStatus.set("idle");
}

/**
 * Swim-aware reset (audit 2026-07-12, false "didn't save" pill on Sets->Swim
 * remount): Prototypes' firehose effect calls this on EVERY (re)load,
 * including the Swim panel remounting on the SAME swim after a tab switch —
 * not just a genuine swim-switch. resetSaveStatus()'s job is to invalidate a
 * stale in-flight commit when the DISPLAYED swim is about to change (T5b); on
 * a same-swim remount there is no new swim to protect, and disarming
 * saveStatus's "saving" mid-write is exactly what let showSaveTrouble fire on
 * a save that was healthy and in flight (saveIndicator.ts's ONLY suppression
 * for label saves is `saveStatus === "saving"`). Mirrors resolveLabelsOnLoad's
 * same-swim-trusts-memory reasoning just below: a remount of the swim already
 * in memory must not disturb what's in flight for it.
 */
export function resetSaveStatusForSwim(swimUuid: string | undefined): void {
  if (swimUuid !== undefined && swimUuid === lastResetSwimUuid) return;
  lastResetSwimUuid = swimUuid;
  resetSaveStatus();
}

/**
 * Resolve the labels to seed when (re)loading `swimUuid`'s firehose.
 *
 * ROOT of the Sets-tab revert bug (fix/tab-switch-label-revert): the Swim panel
 * unmounts on every tab switch and remounts on return, re-running Prototypes'
 * firehose fetch effect. That effect used to ALWAYS re-read labels from native
 * (getLapLabels) and publish them over currentLabels / currentDerivedWorkout. A
 * fresh native read can lag the optimistic write the user just committed (the
 * write is async; the read-back confirms it only later), so re-deriving from it
 * reverted the correction the in-memory store already held.
 *
 * currentLabels is the single source of truth (state.ts). When the swim being
 * loaded is the SAME one already reflected in memory (currentRawFirehose), trust
 * it: it always reflects every correction (commitLabelChange sets it
 * synchronously) and is never staler than disk for this app — only this app
 * writes labels, always through commitLabelChange. A DIFFERENT swim has no
 * in-memory truth yet, so it reads native exactly as before (bounded retry).
 */
export async function resolveLabelsOnLoad(
  swimUuid: string,
  opts: LabelLoadRetryOptions = {},
): Promise<{ labels: LapLabel[]; failed: boolean }> {
  const inMemory = get(currentRawFirehose);
  if (inMemory && inMemory.workout.uuid === swimUuid) {
    return { labels: get(currentLabels), failed: get(labelsLoadFailed) };
  }
  return fetchLabelsWithRetry(() => bridge.getLapLabels(swimUuid), opts);
}

/**
 * Re-read the persisted labels for the swim ALREADY on screen and re-publish
 * them over `currentLabels` / `currentDerivedWorkout`.
 *
 * Native tab bar (roadmap item 38): each tab is its own web view with its own
 * JS heap, so a correction committed in the SWIM view is invisible to the
 * SCORES view's copy of these stores — and `resolveLabelsOnLoad` will not
 * rescue it, because that function deliberately trusts in-memory labels for the
 * swim it already holds (the tab-switch revert fix). Here the trust is inverted
 * on purpose: this is called from `swimset:tab-appeared`, i.e. precisely when
 * another view may have written since we last read. UserDefaults is the shared
 * truth between the heaps; this is the read.
 *
 * A failed read keeps the labels on screen (never blanks them) but is NOT
 * silent (stroke-sync 2026-09-12). This heap may be holding labels older than
 * what another tab saved, and every write is a full replacement, so a
 * correction made here would erase the other tab's. After the read-back ladder
 * is exhausted the swim is marked read-blocked (`labelsLoadFailed`, the C2a
 * guard: commitLabelChange refuses and shows the failure) and the reason lands
 * in the save log. The next successful refresh clears it; a blocked commit
 * starts that refresh itself.
 */
export async function refreshLabelsFromNative(
  opts: LabelLoadRetryOptions = {},
): Promise<boolean> {
  const raw = get(currentRawFirehose);
  if (!raw) return false;
  // Read-back ladder. Before 2026-09-12 this was ONE 400 ms retry and a silent
  // give-up, which left a stale heap free to overwrite a newer correction.
  const { labels, failed } = await fetchLabelsWithRetry(
    () => bridge.getLapLabels(raw.workout.uuid),
    { backoffsMs: REFRESH_BACKOFFS_MS, ...opts },
  );
  if (failed) {
    // Only for the swim still on screen: a swim switch mid-read owns its own load.
    if (get(currentRawFirehose) === raw) {
      labelsLoadFailed.set(true);
      recordOutcome(0, false, "refresh-read-failed", raw.workout.uuid);
    }
    return false;
  }
  // The swim on screen can have CHANGED while this read was in flight — the
  // shell delivers a cross-tab swim selection and a tab-appeared nudge in the
  // same turn, and this read has a 400 ms retry. Publishing a pre-await
  // snapshot would show the old swim's labels under the new swim's uuid, and
  // the next correction would write them there. Clearing `labelsLoadFailed`
  // would also re-arm the very overwrite C2a exists to block.
  if (get(currentRawFirehose) !== raw) return false;
  labelsLoadFailed.set(false);
  // The common case for a tab appearing is that nothing changed in the other
  // heap. `deriveWorkout` re-runs pool detection, lap building, stroke overrides
  // and suspect finding over every length, and publishing re-renders the ribbon,
  // the rings and every per-swim section — so do it only on an actual delta.
  if (labelsEqual(labels, get(currentLabels))) return true;
  currentLabels.set(labels);
  currentDerivedWorkout.set(deriveWorkout(raw, labels));
  return true;
}

/** Shallow structural comparison, enough to answer "did the other heap write?". */
function labelsEqual(a: LapLabel[], b: LapLabel[]): boolean {
  return a.length === b.length && JSON.stringify(a) === JSON.stringify(b);
}

/** Mirror the durable journal's size into the reactive store the pill reads. */
export function syncJournalCount(): void {
  journalPendingCount.set(journalCount());
}

/**
 * TEST-ONLY: reset EVERY module-private binding to its first-load value so a
 * test starting fresh cannot inherit a prior test's leaked in-flight write,
 * parked pending slot, replay latch, commit counters, or save-outcome log.
 * resetSaveStatus() deliberately leaves inFlight/pending intact (a live swim-switch must not
 * abandon a real write mid-flight), so it is NOT a substitute for this between
 * tests — a hung-then-settled write in one `it` block otherwise leaves
 * inFlight/pending dirty and starves the next block's drain(). Mirrors
 * labelJournal's _clearJournal seam. Not referenced by production code.
 */
export function _resetForTests(): void {
  commitSeq = 0;
  latestCommitId = 0;
  inFlight = false;
  pending = null;
  replayPromise = null;
  saveLog.length = 0;
  disarmAutoRetry();
  lastResetSwimUuid = undefined; // else a prior test's swim uuid leaks in and silently no-ops this one's reset
}

/**
 * RECONCILE every correction still sitting in the durable journal against
 * native truth (2026-07-09 escalation: replay is a merge, never a verbatim
 * re-send — see doReplayJournal's pre-read + mergeLabelsForReplay). Called on
 * boot and on the auth-resolved nudge, and by the save-trouble pill's retry.
 * Entries are processed sequentially (single bridge write in flight at a
 * time), so the live latest-wins queue is never fought. The journal holds AT
 * MOST ONE entry per swim (appendJournal supersedes any existing entry for
 * the same workoutUUID before adding the new one) — a stale failed-write
 * entry is evicted the moment a later correction for that swim is journaled,
 * not merely on confirm. Removal matches on workoutUUID AND commitId
 * together, so a cross-session commitId collision (commitSeq resets to 0 on
 * relaunch) cannot evict an unrelated swim's entry. A failed entry is LEFT in
 * the journal to retry on the next boot/nudge. NOTE: replay does NOT route
 * through the single-slot pending queue on purpose — that slot coalesces to
 * one swim, which would drop replays for other swims. Each write is read
 * back and confirmed before its journal entry is removed (see
 * verifyReadBack) — an unverified write is left in place for the next
 * boot/nudge, same as an outright bridge rejection.
 *
 * Per-entry membership re-check (I-1, 2026-07-06 final review): the for-of
 * loop below iterates a readJournal() SNAPSHOT taken once at loop start.
 * Supersession alone does not make replay idempotent across the WHOLE pass —
 * a live commit to a different, already-snapshotted swim can supersede that
 * swim's entry while an earlier entry in the snapshot is still being posted.
 * Dispatching the now-superseded snapshot entry afterward would land it on
 * disk AFTER the fresh write already confirmed, and that stale write's own
 * read-back would then verify the stale value as "correct" — a silent
 * clobber through the recovery machinery itself. Each iteration therefore
 * re-checks the entry is still in the current journal (by workoutUUID +
 * commitId) immediately before dispatching, and skips (recording
 * "replay-superseded") if it was evicted since the snapshot was taken.
 *
 * Reentrancy guard (2026-07-06 fix round): replayJournal is called from boot,
 * the auth-resolved nudge, AND the save-trouble pill's "Try now" button — an
 * impatient extra tap must not launch a SECOND overlapping pass. This path
 * intentionally bypasses the single-flight commit queue above (each replay
 * takes its own readJournal() snapshot to cover every stranded swim, not just
 * one), so without a guard two concurrent calls would each dispatch every
 * entry independently. A call made while one is already running is handed the
 * SAME in-flight promise rather than either no-op'ing (caller can't tell when
 * it's done) or starting a redundant pass.
 */
let replayPromise: Promise<void> | null = null;

export function replayJournal(): Promise<void> {
  if (replayPromise) return replayPromise;
  journalReplaying.set(true);
  replayPromise = doReplayJournal().finally(() => {
    replayPromise = null;
    journalReplaying.set(false);
  });
  return replayPromise;
}

async function doReplayJournal(): Promise<void> {
  if (get(showcase)) return; // public page never writes
  for (const e of readJournal()) {
    // NATIVE-TRUTH PRE-READ (2026-07-09 escalation — the 11→7 label
    // regression). A journal entry is a full-replacement snapshot frozen at
    // commit time; it can be arbitrarily stale relative to native (a strand
    // from an earlier session — or the OTHER localStorage origin, remote vs
    // bundled fallback, where per-swim supersession never saw the later
    // corrections). setLapLabels is last-write-wins, so replaying it verbatim
    // clobbers every label native gained since. Read native's current list
    // FIRST and reconcile; if the read cannot be performed, write NOTHING —
    // an uncomparable snapshot must never be allowed to destroy newer truth.
    // The entry survives for the next boot/nudge, and the failure names
    // itself ("replay-preread-*") instead of stranding silently.
    let stored: LapLabel[];
    try {
      const raw = await bridge.getLapLabels(e.workoutUUID);
      if (!Array.isArray(raw)) throw new Error("getLapLabels resolved to a non-array");
      stored = raw;
    } catch (err) {
      recordOutcome(e.commitId, false, `replay-preread-${bridgeReasonTag(err) ?? "rejected"}`, e.workoutUUID, errorDetail(err));
      continue;
    }
    // A live commit during this pass (or during the pre-read await above) may
    // have superseded this entry — appendJournal's per-swim supersession runs
    // synchronously, so this is always up to date. No await sits between this
    // check and the post below, so JS's single-threaded microtask ordering
    // makes the window uninterleavable by a concurrent live commit (I-1).
    const stillJournaled = readJournal().some(
      (c) => c.workoutUUID === e.workoutUUID && c.commitId === e.commitId,
    );
    if (!stillJournaled) {
      recordOutcome(e.commitId, false, "replay-superseded", e.workoutUUID);
      continue;
    }
    const merged = mergeLabelsForReplay(stored, e.labels);
    if (sameLabelSet(merged, stored)) {
      // Native already holds (or newer-supersedes) everything in this
      // snapshot — there is nothing to recover. Drain WITHOUT writing: the
      // no-op write would only re-expose the last-write-wins race.
      recordOutcome(e.commitId, true, "replay-subsumed", e.workoutUUID);
      removeJournal(e.workoutUUID, e.commitId);
      continue;
    }
    try {
      await bridge.setLapLabels(merged, e.workoutUUID);
    } catch (err) {
      // Boot/locker-room recovery path (Task 6 fix round): every replay
      // attempt now names itself in the save log, same as a live commit —
      // this path previously produced zero ring-buffer entries on failure.
      recordOutcome(e.commitId, false, `replay-${bridgeReasonTag(err) ?? "rejected"}`, e.workoutUUID, errorDetail(err));
      // Reply-lost rescue, replay flavor (2026-07-09 — see attempt() above):
      // launch contention can race out the reply of a replay write that
      // native persisted. Without this, the entry re-strands and re-pills on
      // EVERY boot/nudge whose reply loses the race, while the fix is on disk
      // the whole time. Same polarity rules as the live path.
      if (bridgeReasonTag(err) === "timeout") {
        const rescued = await verifyReadBack(merged, e.workoutUUID);
        if (rescued.ok) {
          recordOutcome(e.commitId, true, "replay-timeout-recovered", e.workoutUUID);
          removeJournal(e.workoutUUID, e.commitId);
          continue;
        }
      }
      continue; // leave in journal; retry next boot/nudge
    }
    // The replay write RESOLVED above → durably on disk. A read-back that
    // RESOLVED-but-mismatched is a real integrity signal (retry later); a
    // read-back that couldn't be PERFORMED (unreadable) says nothing about the
    // write that already landed, so it must NOT strand the entry and re-pill
    // every boot/nudge (2026-07-08 audit). Verification target is the MERGED
    // list — that is what was written.
    const verified = await verifyReadBack(merged, e.workoutUUID);
    if (!verified.ok && verified.kind === "mismatch") {
      recordOutcome(e.commitId, false, `replay-${verified.reason}`, e.workoutUUID, verified.detail);
      continue; // real mismatch — retry later
    }
    recordOutcome(e.commitId, verified.ok, verified.ok ? "replay-ok" : `replay-${verified.reason}`, e.workoutUUID);
    removeJournal(e.workoutUUID, e.commitId);
  }
  syncJournalCount();
}